// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the faster-than-light flight between two bodies (owner,
 * 2026-11-04; docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md).
 *
 * Nobody picks a speed. The flight's length follows the distance: the
 * shortest trip there is (the Earth to the Moon, 384,400 km) takes half a
 * second, the longest (the Sun to Pluto at its farthest, 49.3 AU) five,
 * and every trip falls between them in proportion to its distance on a
 * logarithmic scale. A linear scale would squeeze every trip inside
 * Saturn's orbit under a second and a half; on this one the Sun is 3.2 s
 * from the Earth, Jupiter about 4 s and Neptune about 4.9 s. Trips longer
 * than the Sun–Pluto one (Pluto to a planet on the far side) take the five
 * seconds too.
 *
 * On arrival a short message says how fast that was: the light-time of
 * the distance over the flight's length, as a multiple of the speed of
 * light.
 *
 * The path: the remaining distance shrinks geometrically (a straight line
 * in log-distance, eased at both ends), so the target grows steadily on
 * the eye the whole way instead of popping up in the last instant, as it
 * would at a constant speed.
 */

import { AU_KM } from './ephemeris'
import { PLUTO_APHELION_AU } from './layout'

export const LIGHT_KM_S = 299_792.458
export const FLIGHT_MIN_S = 0.5
export const FLIGHT_MAX_S = 5
/** The Earth–Moon mean distance, km: the shortest flight. */
export const FLIGHT_MIN_KM = 384_400
/** The Sun to Pluto at its farthest, km: the longest flight. */
export const FLIGHT_MAX_KM = PLUTO_APHELION_AU * AU_KM

/** How long a flight over `distanceKm` lasts, seconds (0.5–5). */
export function flightSeconds(distanceKm: number): number {
  if (!(distanceKm > FLIGHT_MIN_KM)) return FLIGHT_MIN_S
  const f = Math.log(distanceKm / FLIGHT_MIN_KM) / Math.log(FLIGHT_MAX_KM / FLIGHT_MIN_KM)
  return Math.min(FLIGHT_MAX_S, FLIGHT_MIN_S + (FLIGHT_MAX_S - FLIGHT_MIN_S) * f)
}

/** How long light takes over `distanceKm`, seconds. */
export function lightSeconds(distanceKm: number): number {
  return distanceKm / LIGHT_KM_S
}

/** The flight's speed as a multiple of the speed of light. */
export function lightSpeedMultiple(distanceKm: number): number {
  return lightSeconds(distanceKm) / flightSeconds(distanceKm)
}

/** A light-time in hours, minutes and seconds, for the arrival message. */
export function splitDuration(seconds: number): { h: number; m: number; s: number } {
  const total = Math.max(0, seconds)
  if (total < 60) return { h: 0, m: 0, s: Math.round(total * 10) / 10 }
  const whole = Math.round(total)
  return { h: Math.floor(whole / 3600), m: Math.floor((whole % 3600) / 60), s: whole % 60 }
}

/** Ease at both ends (smootherstep). */
export function easeFlight(u: number): number {
  const x = Math.min(1, Math.max(0, u))
  return x * x * x * (x * (x * 6 - 15) + 10)
}

/**
 * The distance still to go at a moment of the flight (`u` 0..1), from
 * `startKm` to `endKm` (where the target fills the view), geometric in
 * between.
 */
export function remainingKm(u: number, startKm: number, endKm: number): number {
  const s = easeFlight(u)
  return Math.exp(Math.log(startKm) * (1 - s) + Math.log(Math.max(1e-9, endKm)) * s)
}

/**
 * How fast the flight looks at `u`, 0..1 (the log-distance's rate, peak
 * 1): drives the length of the star streaks.
 */
export function streakStrength(u: number): number {
  const x = Math.min(1, Math.max(0, u))
  // d/du of smootherstep, 30 x²(1 − x)², peaks at 1.875 at x = 0.5.
  return (30 * x * x * (1 - x) * (1 - x)) / 1.875
}
